/* oxlint-disable no-console */

import { InstrumentProductTypeEnum } from '@flipster/apis/v2/snapshot';
import type { RequestIntervalSnapshotFn, ToggleSocketIntervalDataFn } from '@flipster/r230522/original/interval/socket';
import { datadog, tradeLogger } from '@flipster/utils/datadog';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { atomicChain } from '@flipster/utils/functional/atomic-chain';
import sleep from '@flipster/utils/sleep';

import {
  Bar,
  DatafeedErrorCallback,
  HistoryCallback,
  IBasicDataFeed,
  LibrarySymbolInfo,
  OnReadyCallback,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
  SymbolResolveExtension,
} from '../../lib/charting_library';
import { Block, disposeBarsUpdater, getOrCreateBarsUpdater } from '../bars';
import { Period } from '../bars/chunks';
import { isLastBasedChartPrice, toIntervalTransportPriceType } from '../misc/chart-price-type';
import { SupportedResolutions } from '../misc/constants';
import { SymbolInfoBase, getSymbols, symbolInfoBase } from '../misc/data';
import { delayCall, getTimeByResolution, resolutionToSecs, secsToResolution, symbolNameToInfo } from '../misc/helper';

const dummySymbolInfoBase = symbolInfoBase({
  symbol: 'dummy',
  productType: InstrumentProductTypeEnum.Invalid,
  pricePrecision: 0,
});

export type ResolveSymbolInfoBase = (
  symbolName: string,
) => Promise<SymbolInfoBase | undefined> | SymbolInfoBase | undefined;

interface Subscription {
  refCount: number;
  readonly symbolName: string;
  readonly resolution: number;
}
const subscriptionTable: Record<string, Subscription> = {};

interface Datafeed extends IBasicDataFeed {
  isWebview?: boolean;
  resolveSymbolInfoBase?: ResolveSymbolInfoBase;
  requestIntervalSnapshot?: RequestIntervalSnapshotFn;
  subscribeSocketIntervalData?: ToggleSocketIntervalDataFn;
  unsubscribeSocketIntervalData?: ToggleSocketIntervalDataFn;
}

interface DatafeedEvents {
  onNoData: { symbol: string; period: Period };
  symbolNotResolved: string;
}

export class DatafeedHandler extends EventEmitter<DatafeedEvents> implements Datafeed {
  public isWebview = false;

  public resolveSymbolInfoBase: ResolveSymbolInfoBase | undefined = undefined;
  public requestIntervalSnapshot: RequestIntervalSnapshotFn | undefined = undefined;
  public subscribeSocketIntervalData: ToggleSocketIntervalDataFn | undefined = undefined;
  public unsubscribeSocketIntervalData: ToggleSocketIntervalDataFn | undefined = undefined;

  public constructor() {
    super();
  }

  public getBars = async (
    { name }: LibrarySymbolInfo,
    resolutionString: ResolutionString,
    period: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback,
  ): Promise<void> => {
    const symbolName = name;
    try {
      if (name.startsWith('dummy')) {
        return await sleep(0).then(() => onResult([], { noData: true }));
      }
      const resolution = resolutionToSecs(resolutionString);
      const barsUpdater = getOrCreateBarsUpdater(symbolName, resolution);
      if (period.firstDataRequest) {
        datadog.setContext('firstDataRequest_symbol', symbolName);
        datadog.setContext('firstDataRequest_currentTime', new Date().toISOString());
        datadog.setContext('firstDataRequest_resolution', resolution);
        datadog.setContext('firstDataRequest_periodTo', period.to);
        datadog.setContext('firstDataRequest_periodCountBack', period.countBack);
      }
      const calculatedPeriod = Block.calcPeriod(period.countBack, resolution, period.to);
      const { bars } = await barsUpdater.fetch(calculatedPeriod);
      const noData = bars.length < 1;
      if (period.firstDataRequest && noData) {
        const time = getTimeByResolution(Date.now(), resolutionString);
        this.emit('onNoData', { symbol: symbolName, period: calculatedPeriod });
        onResult([{ time, open: 0, high: 0, low: 0, close: 0, volume: 0 }]);
      } else {
        onResult(bars, { noData });
      }
    } catch (err) {
      console.error('[getBars]: Get error', err);
      onError(typeof err === 'string' ? err : 'unknown error n/s');
    }
  };

  public onReady = (callback: OnReadyCallback): void => {
    delayCall(() =>
      callback({
        supported_resolutions: SupportedResolutions,
        supports_timescale_marks: false,
      }),
    );
  };

  public resolveSymbol = async (
    symbolName: string,
    onResolve: ResolveCallback,
    onError: DatafeedErrorCallback,
    _extension?: SymbolResolveExtension,
  ): Promise<void> => {
    try {
      const symbolInfo = await (() => {
        if (symbolName.startsWith('dummy')) {
          // synchronous하게 반환하면 tvchart가 콘솔에 setTimeout(0)을 쓰라고 워닝을 남기므로 (...왜??)
          // sleep(0)으로 우회
          return sleep(0).then(() => dummySymbolInfoBase);
        }
        return this.resolveSymbolInfoBase?.(symbolName);
      })();
      if (!symbolInfo) throw new Error('Cannot resolve symbol');

      const { symbol, priceType } = symbolNameToInfo(symbolName);
      const ticker = isLastBasedChartPrice(priceType) ? symbol : symbolName;
      onResolve({
        ...symbolInfo,
        name: symbolName, // search 시 사용될 이름
        ticker, // tv 왼쪽위 나오는 이름
        session: '24x7',
        format: 'price',
        listed_exchange: 'Flipster',
        // The time zone of the exchange where the symbol is listed.
        // https://www.tradingview.com/charting-library-docs/latest/api/interfaces/Charting_Library.LibrarySymbolInfo/#timezone
        timezone: 'Etc/UTC',
        minmov: 1,
        has_intraday: true,
        has_weekly_and_monthly: true,
        supported_resolutions: SupportedResolutions,
        volume_precision: 0,
        data_status: 'streaming',
        visible_plots_set: 'ohlcv',
        library_custom_fields: {
          quoteCurrency: symbolInfo.quoteCurrency,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // 심볼 해석 실패를 근원지에서 정확한 symbolName/원본 에러와 함께 기록함.
      // webview에서는 이 실패가 차트 리로드로 이어지므로 reload 이벤트명으로, web에서는 중립적 이름으로 로깅함.
      tradeLogger.warn(
        this.isWebview ? 'chart_need_to_reload' : 'chart_symbol_not_resolved',
        {
          error_message: message,
          error_category: 'http_errors',
          symbol: symbolName,
          is_webview: this.isWebview,
          url: window.location.href,
        },
        err,
      );
      this.emit('symbolNotResolved', message);
      onError(message);
    }
  };

  // This method is used by the Charting Library to search symbols every time
  // a user types a text in the symbol search box.
  // Changing symbols is also works using the symbol search.
  public searchSymbols = async (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResult: SearchSymbolsCallback,
  ): Promise<void> => {
    const symbols = await getSymbols();
    const newSymbols = symbols.filter(
      (symbol) => symbol.exchange === exchange && symbol.symbol.toLowerCase().includes(userInput.toLowerCase()),
    );
    onResult(newSymbols);
  };

  public subscribeBars = (
    { name }: LibrarySymbolInfo,
    resolutionString: ResolutionString,
    onTick: SubscribeBarsCallback,
    id: string,
    _onResetCacheNeededCallback: () => void,
  ): void => {
    const symbolName = name;
    if (symbolName.startsWith('dummy')) return;
    const { symbol, priceType } = symbolNameToInfo(symbolName);
    const resolution = resolutionToSecs(resolutionString);
    const barsUpdater = getOrCreateBarsUpdater(symbolName, resolution);

    const ticker = async (bar: Bar) => {
      return onTick(bar);
    };

    barsUpdater.onTick = atomicChain(ticker);
    if (this.requestIntervalSnapshot) {
      barsUpdater.requestIntervalSnapshot = this.requestIntervalSnapshot;
    } else {
      tradeLogger.warn('chart_data_missing', {
        error_category: 'javascript_errors',
        error_message: 'requestIntervalSnapshot is not available',
        symbol: symbolName,
        timeframe: resolutionString,
        url: window.location.href,
      });
    }

    if (subscriptionTable[id]) {
      subscriptionTable[id].refCount += 1;
    } else {
      subscriptionTable[id] = { symbolName, resolution, refCount: 1 };
      this.subscribeSocketIntervalData?.(symbol, resolutionString, toIntervalTransportPriceType(priceType));
    }
  };

  public unsubscribeBars = (id: string): void => {
    if (!(id in subscriptionTable)) return;
    const { symbolName, resolution } = subscriptionTable[id];
    const { symbol, priceType } = symbolNameToInfo(symbolName);
    const resolutionString = secsToResolution(resolution);

    subscriptionTable[id].refCount -= 1;
    if (subscriptionTable[id].refCount > 0) return;

    disposeBarsUpdater(symbolName, resolution);

    delete subscriptionTable[id];
    this.unsubscribeSocketIntervalData?.(symbol, resolutionString, toIntervalTransportPriceType(priceType));
  };
}

export const datafeed = new DatafeedHandler();
